import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Button, Icon, formatNumber } from '@column/column-ui-kit';

import { usePinBankAccounts } from '../../BankAccounts/_hooks/usePinBankAccounts';
import { ROUTE } from '~/app/routes';
import { useViewTransition } from '~/hooks/useViewTransition';
import { useNavigate } from '~/lib/navigation';
import { Card, Headline, Paragraph } from '~/styles';
import { breakpoints } from '~/Theme/breakpoints';

import { PinnedBankAccountsModal } from './PinnedBankAccountsModal';

const Wrapper = styled.div`
  position: relative;
  padding: 24px 0;
  overflow: hidden;
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--page-margin) 16px;
  height: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Controls = styled.div`
  display: flex;
`;

const Arrow = styled(Button)`
  &:first-child {
    --button-border-radius: 8px 0 0 8px;
    margin-right: -1px;
  }

  &:last-child {
    --button-border-radius: 0 8px 8px 0;
  }

  &:hover {
    z-index: 1;
  }
`;

const CardsContainer = styled.div`
  position: relative;
  padding: 0 var(--page-margin);
  margin-bottom: -12px;
  width: 100%;
  display: grid;
  grid-template-columns: auto;
  box-sizing: border-box;
  mask-image: linear-gradient(to right, transparent, black 24px, black calc(100% - 24px), transparent 100%);
  --cards-per-view: 1;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    --cards-per-view: 2;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    --cards-per-view: 3;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    --cards-per-view: 4;
  }
`;

const CardWrapper = styled.div<{ $currentIndex: number }>`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: calc((100% - (var(--cards-per-view) - 1) * 16px) / var(--cards-per-view));
  grid-template-rows: max-content;
  gap: 16px;
  transform: translateX(calc(${({ $currentIndex }) => $currentIndex} * (-100% - 16px) / var(--cards-per-view)));
  min-width: 0;
  transition: transform 0.3s ease-in-out;
`;

const CardTitle = styled.div`
  transition: color 0.2s;
  font-size: 14px;
  max-width: calc(100% - 32px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  line-height: 20px;
  color: ${({ theme }) => theme.secondary.blendToBackground(900)};
`;

const CardArrow = styled(Icon.ChevronRight)`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translate(var(--card-arrow-x, 0px), -50%) translateZ(0);
  color: var(--card-arrow-color, ${({ theme }) => theme.secondary.blendToBackground(600)});
  transition:
    transform 0.2s,
    color 0.2s;
`;

const StyledCard = styled(Card)`
  cursor: pointer;
  position: relative;
  padding: 12px;
  margin: 1px 0 12px;
  box-sizing: border-box;
  min-height: 58px;
  background-color: ${({ theme }) => theme.secondary.blendToBackground(40)};
  box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(150)};
  transition:
    background-color 0.2s,
    box-shadow 0.2s;

  &:hover {
    --card-arrow-x: 4px;
    --card-arrow-color: ${({ theme }) => theme.primary.background};

    background-color: ${({ theme }) => theme.background};
    box-shadow:
      ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
        ${({ theme }) => theme.style.buttonSecondaryBorderWidth}
        ${({ theme }) => theme.style.buttonSecondaryBorderColor},
      0 0 0 var(--button-outline, 0px)
        var(--button-outline-color, ${({ theme }) => theme.style.buttonFocussedOutlineColor}),
      ${({ theme }) => theme.style.buttonSecondaryShadow};

    ${CardTitle} {
      color: ${({ theme }) => theme.primary.background};
    }
  }
`;

const CardPlus = styled(Icon.Plus)`
  --icon-size: 16px;

  transition:
    transform 0.3s,
    color 0.2s;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  transform: translate(-50%, -50%) scale(var(--card-plus-scale, 1)) translateZ(0);
  position: absolute;
  top: 50%;
  left: 50%;
`;

const EmptyCard = styled(StyledCard)`
  background-color: ${({ theme }) => theme.secondary.blendToBackground(20)};
  box-shadow: none;
  user-select: none;

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    border: 1px dashed ${({ theme }) => theme.secondary.blendToBackground(125)};
    transition: opacity 0.2s;
  }

  &:hover {
    --card-plus-scale: 1.3;
  }

  &:hover {
    ${CardPlus} {
      color: ${({ theme }) => theme.primary.background};
    }

    &:before {
      opacity: 0;
    }
  }
`;

const Empty = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto;
  gap: 16px;
  padding: 0 var(--page-margin);

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 280px auto;
    gap: 24px;
  }
`;

const EmptyList = styled.div`
  position: relative;
  width: 100%;
  height: 164px;
  flex-shrink: 0;
  padding: 24px 8px;
  border-radius: 13px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.secondary.blendToBackground(25)};
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(125)};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const EmptyEntry = styled.div`
  display: grid;
  align-items: start;
  position: relative;
  width: 164px;
  gap: 8px;
  background-color: ${({ theme }) => theme.background};
  border-radius: 7px;
  padding: 16px;
  border: 1px solid hsla(230, 30%, 5%, 0.1);
  backdrop-filter: blur(4px);

  &:first-child {
    z-index: 1;
    margin: 0 auto;

    svg {
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translateY(calc(-50% + 2px));
      color: ${({ theme }) => theme.secondary.blendToBackground(500)};

      --icon-size: 18px;
    }
  }

  &:nth-child(2),
  &:nth-child(3) {
    position: absolute;
    left: 50%;
    top: 50%;
    background-color: ${({ theme }) => theme.secondary.blendToBackground(0, 300)};
    border: 1px solid hsla(230, 30%, 5%, 0.05);

    span {
      opacity: 0.5;
    }
  }

  &:nth-child(2) {
    transform: translate(calc(-50% - 58px), calc(-50% - 24px)) rotate(-12deg) scaleX(0.7) scaleY(0.8);
  }

  &:nth-child(3) {
    left: auto;
    right: 50%;
    transform: translate(calc(50% + 56px), calc(-50% - 8px)) rotate(4deg) scaleX(0.7) scaleY(0.8);
  }

  span {
    background-color: ${({ theme }) => theme.secondary.blendToBackground(400)};
    border-radius: 3px;
    height: 10px;
    width: 15%;

    &:nth-child(2) {
      height: 6px;
      width: 60%;
      border-radius: 2px;
      background-color: ${({ theme }) => theme.secondary.blendToBackground(200)};
    }
  }
`;

const EmptyContent = styled.div`
  display: grid;
  gap: 8px;
  max-width: 400px;
`;

const CardAmount = styled.div`
  color: ${({ theme }) => theme.foreground};
  font-size: 16px;
  line-height: 20px;
  font-weight: 500;
  margin-top: 4px;
`;

export const PinnedBankAccounts: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const navigate = useNavigate();
  const { pinnedAccounts } = usePinBankAccounts();

  const containerRef = useRef<HTMLDivElement>(null);
  const [cardsPerView, setCardsPerView] = useState(1);

  useEffect(() => {
    const tabletBreakpoint = breakpoints.tablet;
    const desktopBreakpoint = breakpoints.desktop;
    const wideBreakpoint = breakpoints.wide;

    const calculateCardsPerView = () => {
      if (window.matchMedia(`(min-width: ${wideBreakpoint})`).matches) {
        return 4;
      }
      if (window.matchMedia(`(min-width: ${desktopBreakpoint})`).matches) {
        return 3;
      }
      if (window.matchMedia(`(min-width: ${tabletBreakpoint})`).matches) {
        return 2;
      }
      return 1;
    };

    const updateCardsPerView = () => {
      const newCardsPerView = calculateCardsPerView();
      setCardsPerView(newCardsPerView);
    };
    const tabletMediaQuery = window.matchMedia(`(min-width: ${tabletBreakpoint})`);
    const desktopMediaQuery = window.matchMedia(`(min-width: ${desktopBreakpoint})`);
    const wideMediaQuery = window.matchMedia(`(min-width: ${wideBreakpoint})`);

    tabletMediaQuery.addEventListener('change', updateCardsPerView);
    desktopMediaQuery.addEventListener('change', updateCardsPerView);
    wideMediaQuery.addEventListener('change', updateCardsPerView);

    updateCardsPerView();

    return () => {
      tabletMediaQuery.removeEventListener('change', updateCardsPerView);
      desktopMediaQuery.removeEventListener('change', updateCardsPerView);
      wideMediaQuery.removeEventListener('change', updateCardsPerView);
    };
  }, []);

  const maxIndex = Math.max(0, pinnedAccounts.length - cardsPerView);

  const onPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => Math.max(0, prev - 1));
    }
  }, [currentIndex]);

  const onNext = useCallback(() => {
    if (currentIndex < maxIndex) {
      setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
    }
  }, [currentIndex, maxIndex]);

  const onEdit = useCallback(() => {
    setIsOpen(true);
  }, []);

  const isEmpty = useViewTransition(pinnedAccounts && pinnedAccounts.length <= 0);

  return (
    <Wrapper>
      <PinnedBankAccountsModal isOpen={isOpen} onClose={() => setIsOpen(false)} cardsPerView={cardsPerView} />
      <Toolbar>
        <Headline size="small">Pinned Accounts</Headline>

        <ButtonGroup>
          {pinnedAccounts.length > 0 && (
            <Button variant="muted" size="small" onClick={onEdit}>
              Edit
            </Button>
          )}

          <Controls>
            <Arrow
              variant="muted"
              size="small"
              icon={<Icon.ChevronLeft />}
              onClick={onPrev}
              isDisabled={currentIndex === 0}
              isFocussed={false}
            />

            <Arrow
              variant="muted"
              size="small"
              icon={<Icon.ChevronRight />}
              onClick={onNext}
              isDisabled={currentIndex >= maxIndex}
              isFocussed={false}
            />
          </Controls>
        </ButtonGroup>
      </Toolbar>

      {isEmpty ? (
        <Empty>
          <EmptyList>
            {Array.from({ length: 3 }).map((_, i) => (
              <EmptyEntry key={i}>
                <span />
                <span />

                {i === 0 && <Icon.Pin />}
              </EmptyEntry>
            ))}
          </EmptyList>

          <EmptyContent>
            <Headline size="small">No pinned accounts</Headline>
            <Paragraph>
              Pinned accounts provide a quick access to your bank accounts. Save your most used accounts.
            </Paragraph>
            <div>
              <Button
                variant="secondary"
                size="small"
                onClick={() => navigate(ROUTE.BANK_ACCOUNTS)}
                icon={<Icon.Plus />}
              >
                Accounts
              </Button>
            </div>
          </EmptyContent>
        </Empty>
      ) : (
        <CardsContainer ref={containerRef}>
          <CardWrapper $currentIndex={currentIndex}>
            {pinnedAccounts.map((bankAccount) => (
              <StyledCard
                key={bankAccount.id}
                as="div"
                onClick={() => navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${bankAccount.id}`)}
              >
                <CardTitle>{bankAccount.displayName || bankAccount.description}</CardTitle>
                <CardAmount>{formatNumber(bankAccount.balances?.availableAmount)}</CardAmount>
                <CardArrow />
              </StyledCard>
            ))}
            {Array.from({ length: Math.max(0, cardsPerView - pinnedAccounts.length) }).map((_, index) => (
              <EmptyCard key={`empty-${index}`} as="div" onClick={() => navigate(ROUTE.BANK_ACCOUNTS)}>
                <CardPlus />
              </EmptyCard>
            ))}
          </CardWrapper>
        </CardsContainer>
      )}
    </Wrapper>
  );
};
